import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, Check, Truck, Shield, Leaf, ArrowLeft, Star } from "lucide-react";
import SiteHeader from "@/components/shop/SiteHeader";
import SiteFooter from "@/components/shop/SiteFooter";
import CartDrawer from "@/components/shop/CartDrawer";
import ProductCard from "@/components/shop/ProductCard";
import ProductReviews from "@/components/shop/ProductReviews";
import { api, fmtUSD } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    setActiveImg(0);
    setQty(1);
    api
      .get(`/products/${slug}`)
      .then(({ data }) => {
        setProduct(data);
        setRelated(data.related || []);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <SiteHeader />
        <div className="py-40 text-center text-slate-400" data-testid="product-loading">Loading…</div>
      </div>
    );
  }
  if (notFound || !product) {
    return (
      <div className="min-h-screen bg-white">
        <SiteHeader />
        <div className="py-40 text-center" data-testid="product-not-found">
          <p className="font-display text-2xl font-bold text-slate-900">Product not found</p>
          <button onClick={() => navigate("/shop")} className="mt-4 text-sky-600 underline">Back to shop</button>
        </div>
      </div>
    );
  }

  const onAdd = () => {
    addItem(
      { slug: product.slug, name: product.name, price: product.price, images: product.images },
      qty
    );
  };

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="product-detail-page">
      <SiteHeader />
      <CartDrawer />

      <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-6">
        <Link
          to="/shop"
          className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#173a7a]"
          data-testid="back-to-shop-link"
        >
          <ArrowLeft size={14} /> Back to shop
        </Link>
      </div>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-10 md:py-14 grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Gallery */}
        <div>
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="aspect-square rounded-3xl bg-gradient-to-br from-sky-50 to-white overflow-hidden flex items-center justify-center"
            data-testid="product-hero-image"
          >
            <img src={product.images[activeImg]} alt={product.name} className="w-full h-full object-contain p-8" />
          </motion.div>
          {product.images.length > 1 && (
            <div className="mt-4 flex gap-3">
              {product.images.map((img, i) => (
                <button
                  key={img}
                  data-testid={`thumb-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={`w-20 h-20 rounded-2xl bg-sky-50 p-2 border-2 transition-colors ${
                    activeImg === i ? "border-[#173a7a]" : "border-transparent"
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-contain" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-amber-500 font-bold">
            {product.category.replace(/-/g, " ")}
          </p>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-black text-[#0f2a5c] leading-tight" data-testid="product-title">
            {product.name}
          </h1>
          {product.rating && (
            <div className="mt-2 flex items-center gap-2" data-testid="product-rating">
              <div className="flex items-center gap-0.5">
                {[1,2,3,4,5].map(n => (
                  <Star key={n} size={14} fill={n <= Math.round(product.rating.average || 0) ? "currentColor" : "none"}
                    className={n <= Math.round(product.rating.average || 0) ? "text-amber-400" : "text-slate-300"} />
                ))}
              </div>
              <span className="text-sm text-slate-500">
                {product.rating.count > 0 ? `${product.rating.average.toFixed(1)} · ${product.rating.count} review${product.rating.count === 1 ? "" : "s"}` : "No reviews yet"}
              </span>
            </div>
          )}
          <p className="mt-3 text-lg italic text-slate-600">{product.tagline}</p>

          <div className="mt-6 flex items-baseline gap-3">
            <span className="text-3xl font-black text-slate-900" data-testid="product-price">{fmtUSD(product.price)}</span>
            {product.compare_at && (
              <>
                <span className="text-lg text-slate-400 line-through">{fmtUSD(product.compare_at)}</span>
                <span className="text-sm font-bold text-emerald-600">
                  Save {fmtUSD(product.compare_at - product.price)}
                </span>
              </>
            )}
          </div>

          <p className="mt-6 text-base text-slate-600 leading-relaxed">{product.description}</p>

          <div className="mt-8 space-y-2.5" data-testid="product-features">
            {product.features.map((f) => (
              <div key={f} className="flex items-start gap-3">
                <span className="mt-0.5 w-5 h-5 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0">
                  <Check size={12} strokeWidth={3} />
                </span>
                <span className="text-sm text-slate-700">{f}</span>
              </div>
            ))}
          </div>

          {/* Qty + Add */}
          <div className="mt-8 flex gap-3">
            <div className="flex items-center rounded-full border border-slate-200">
              <button
                data-testid="product-qty-decrease"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="px-4 py-3.5 hover:text-[#173a7a]"
              >
                <Minus size={16} />
              </button>
              <span className="w-10 text-center font-semibold" data-testid="product-qty">{qty}</span>
              <button
                data-testid="product-qty-increase"
                onClick={() => setQty((q) => Math.min(99, q + 1))}
                className="px-4 py-3.5 hover:text-[#173a7a]"
              >
                <Plus size={16} />
              </button>
            </div>
            <button
              data-testid="product-add-to-cart"
              onClick={onAdd}
              className="flex-1 rounded-full bg-[#173a7a] text-white font-semibold py-3.5 hover:bg-[#1a4694] transition-colors"
            >
              Add to Cart — {fmtUSD(product.price * qty)}
            </button>
          </div>

          {/* Trust badges */}
          <div className="mt-8 grid grid-cols-3 gap-3 pt-6 border-t border-slate-100">
            <div className="flex flex-col items-center text-center gap-1.5">
              <Truck size={20} className="text-[#173a7a]" />
              <span className="text-xs text-slate-600 font-semibold">Free shipping over $25</span>
            </div>
            <div className="flex flex-col items-center text-center gap-1.5">
              <Shield size={20} className="text-[#173a7a]" />
              <span className="text-xs text-slate-600 font-semibold">Dermatologist tested</span>
            </div>
            <div className="flex flex-col items-center text-center gap-1.5">
              <Leaf size={20} className="text-[#173a7a]" />
              <span className="text-xs text-slate-600 font-semibold">Made in USA</span>
            </div>
          </div>

          {/* Specs */}
          {product.specs && (
            <div className="mt-8 rounded-2xl border border-slate-100 p-5" data-testid="product-specs">
              <p className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-3">Specifications</p>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                {Object.entries(product.specs).map(([k, v]) => (
                  <div key={k} className="flex justify-between gap-4 py-1 border-b border-slate-50">
                    <dt className="text-slate-500">{k}</dt>
                    <dd className="font-semibold text-slate-900 text-right">{v}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      </section>

      {/* Reviews */}
      <ProductReviews slug={product.slug} rating={product.rating} />

      {/* Related */}
      {related.length > 0 && (
        <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-20">
          <p className="font-display text-2xl md:text-3xl font-bold text-[#0f2a5c] mb-6">You may also like</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {related.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
          </div>
        </section>
      )}

      <SiteFooter />
    </div>
  );
}
